<template>
  <button
    class="service-float"
    :class="{ raised }"
    type="button"
    aria-haspopup="dialog"
    :aria-expanded="panelVisible"
    @click="openPanel"
  >
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M4 13v-1a8 8 0 0 1 16 0v1" />
      <path d="M4 13.5a1.8 1.8 0 0 1 1.8-1.8h.9a1.3 1.3 0 0 1 1.3 1.3v2.4a1.3 1.3 0 0 1-1.3 1.3H5.8A1.8 1.8 0 0 1 4 14.9z" />
      <path d="M20 13.5a1.8 1.8 0 0 0-1.8-1.8h-.9a1.3 1.3 0 0 0-1.3 1.3v2.4a1.3 1.3 0 0 0 1.3 1.3h.9a1.8 1.8 0 0 0 1.8-1.8z" />
      <path d="M18.5 16.7v.8a3 3 0 0 1-3 3H13" />
    </svg>
    <span>客服</span>
  </button>

  <Teleport to="body">
    <Transition name="service-sheet">
      <div v-if="panelVisible" class="service-sheet-mask" @click.self="closePanel">
        <section class="service-sheet" role="dialog" aria-modal="true" aria-labelledby="mobile-service-title">
          <span class="sheet-handle" aria-hidden="true"></span>
          <header>
            <span class="mobile-eyebrow">Customer Service</span>
            <h2 id="mobile-service-title">联系客服</h2>
            <p>扫码添加微信客服，或通过邮箱与我们联系。</p>
          </header>

          <div v-if="loading" class="service-state" aria-live="polite">正在读取客服信息...</div>
          <div v-else-if="error" class="service-state error" role="alert">
            <p>{{ error }}</p>
            <button class="mobile-btn mobile-btn-ghost" type="button" @click="loadServiceConfig(true)">重新加载</button>
          </div>
          <template v-else-if="serviceConfig.customerServiceQrCode || serviceConfig.email">
            <figure v-if="serviceConfig.customerServiceQrCode">
              <img :src="serviceConfig.customerServiceQrCode" alt="客服微信二维码" />
              <figcaption>扫码添加微信客服</figcaption>
            </figure>
            <a v-if="serviceConfig.email" class="service-mail" :href="`mailto:${serviceConfig.email}`">
              <span>联系邮箱</span>
              <strong>{{ serviceConfig.email }}</strong>
            </a>
          </template>
          <p v-else class="service-state">客服信息暂未配置，请稍后再试。</p>

          <button ref="closeButton" class="mobile-btn mobile-btn-ghost" type="button" @click="closePanel">关闭</button>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { getWebsiteProductConfig } from '@/api/trade'

defineProps({
  raised: { type: Boolean, default: false }
})

const panelVisible = ref(false)
const loading = ref(false)
const error = ref('')
const closeButton = ref(null)
const serviceConfig = ref({})
let cachedConfig = null

function lockBody() {
  document.body.style.overflow = 'hidden'
}

function unlockBody() {
  document.body.style.overflow = ''
}

async function loadServiceConfig(force = false) {
  if (!force && cachedConfig) {
    serviceConfig.value = cachedConfig
    return
  }
  loading.value = true
  error.value = ''
  try {
    const config = await getWebsiteProductConfig()
    cachedConfig = {
      customerServiceQrCode: config?.customerServiceQrCode || '',
      email: config?.email || ''
    }
    serviceConfig.value = cachedConfig
  } catch (err) {
    error.value = err?.message || '客服信息加载失败，请稍后重试'
  } finally {
    loading.value = false
  }
}

async function openPanel() {
  panelVisible.value = true
  lockBody()
  await nextTick()
  closeButton.value?.focus()
  if (!cachedConfig) await loadServiceConfig()
}

function closePanel() {
  panelVisible.value = false
  unlockBody()
}

function onKeydown(event) {
  if (event.key === 'Escape' && panelVisible.value) closePanel()
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  unlockBody()
})
</script>

<style scoped>
.service-float {
  position: fixed;
  right: 16px;
  bottom: calc(82px + env(safe-area-inset-bottom));
  z-index: 45;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 42px;
  padding: 0 14px;
  border: 1px solid rgba(98, 244, 214, 0.34);
  border-radius: 999px;
  color: var(--mobile-ink);
  font-size: 12.5px;
  font-weight: 850;
  background: linear-gradient(135deg, rgba(18, 34, 55, 0.96), rgba(6, 15, 28, 0.94));
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.36), 0 0 22px rgba(98, 244, 214, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
}

.service-float.raised {
  bottom: calc(142px + env(safe-area-inset-bottom));
}

.service-float svg {
  color: var(--mobile-cyan);
}

.service-float:active {
  transform: scale(0.97);
  border-color: rgba(98, 244, 214, 0.58);
  background: linear-gradient(135deg, rgba(28, 49, 74, 0.98), rgba(9, 21, 37, 0.96));
}

.service-float:focus-visible,
.service-sheet .mobile-btn:focus-visible,
.service-mail:focus-visible {
  outline: 2px solid var(--mobile-cyan);
  outline-offset: 2px;
}

.service-sheet-mask {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 18px;
  background: rgba(1, 4, 10, 0.72);
  backdrop-filter: blur(6px);
}

.service-sheet {
  width: min(100%, 430px);
  display: grid;
  gap: 16px;
  padding: 14px 18px calc(18px + env(safe-area-inset-bottom));
  border: 1px solid var(--mobile-line-strong);
  border-radius: 22px 22px 0 0;
  background: linear-gradient(170deg, rgba(18, 31, 52, 0.99), rgba(7, 15, 29, 0.99));
  box-shadow: 0 -18px 50px rgba(0, 0, 0, 0.42);
}

.sheet-handle {
  justify-self: center;
  width: 42px;
  height: 4px;
  border-radius: 999px;
  background: rgba(222, 246, 255, 0.18);
}

.service-sheet header {
  display: grid;
  gap: 8px;
}

.service-sheet h2 {
  color: var(--mobile-ink);
  font-size: 19px;
  font-weight: 900;
}

.service-sheet header p {
  margin: -2px 0 0;
  color: var(--mobile-muted);
  font-size: 12px;
  line-height: 1.6;
}

.service-state {
  display: grid;
  gap: 12px;
  color: var(--mobile-muted);
  font-size: 12.5px;
  line-height: 1.7;
  text-align: center;
}

.service-state.error {
  color: #ff9c9c;
}

.service-state .mobile-btn {
  width: 100%;
}

.service-sheet figure {
  display: grid;
  justify-items: center;
  gap: 9px;
  padding: 14px;
  border: 1px dashed var(--mobile-line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
}

.service-sheet img {
  display: block;
  width: 168px;
  height: 168px;
  object-fit: contain;
  border-radius: 12px;
  background: #fff;
}

.service-sheet figcaption {
  color: var(--mobile-muted);
  font-size: 12px;
  font-weight: 700;
}

.service-mail {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 13px 14px;
  border: 1px solid var(--mobile-line);
  border-radius: 15px;
  text-decoration: none;
  background: rgba(98, 244, 214, 0.06);
}

.service-mail span {
  color: var(--mobile-quiet);
  font-size: 11px;
  font-weight: 800;
}

.service-mail strong {
  color: var(--mobile-cyan);
  font-size: 14px;
  font-weight: 900;
  word-break: break-all;
}

.service-sheet-enter-active,
.service-sheet-leave-active {
  transition: opacity 0.18s ease;
}

.service-sheet-enter-from,
.service-sheet-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .service-sheet-enter-active,
  .service-sheet-leave-active {
    transition: none;
  }
}
</style>
